// HomePage/AdminPortal.js
import React, { useState, useEffect } from 'react';
import {
  Modal, Checkbox, Button, Input, ColorPicker, message, Tabs, Card, Space,
  Divider, Switch, Row, Col, Typography, Form, Tree, Popconfirm, Select, Upload, Carousel, Alert, Tag,
} from 'antd';
import {
  BgColorsOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UpOutlined, DownOutlined,
  SaveOutlined,
  ReloadOutlined,
  FormOutlined,
  SettingOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  KeyOutlined,
  UserOutlined,
  LayoutOutlined,
  MenuOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  PictureOutlined,
  UploadOutlined,
  DashboardOutlined,
  LeftOutlined,
  RightOutlined,
  DragOutlined,
  TeamOutlined // Added for Role Manager
} from '@ant-design/icons';
import axios from 'axios';
import RoleManager from './RoleManager';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Default Login Page Theme configuration
const DEFAULT_THEME = {
  pageBg: {
    type: 'gradient',
    gradientStart: '#ffffff',
    gradientEnd: '#a494ee70',
    solidColor: '#f0f2f5'
  },
  imageSectionBg: {
    type: 'gradient',
    gradientStart: '#0183f5',
    gradientEnd: '#060606',
    solidColor: '#1976d2'
  },
  welcomeTitleColor: '#ffffff',
  welcomeSubtitleColor: 'rgba(255, 255, 255, 0.95)',
  formTitleColor: '#0d47a1',
  formSubtitleColor: '#546e7a',
  labelColor: '#37474f',
  loginButtonBg: {
    type: 'gradient',
    gradientStart: '#0d47a1',
    gradientEnd: '#1976d2',
    solidColor: '#0d47a1'
  },
  registerButtonBg: '#ffffff',
  registerButtonTextColor: '#0d47a1',
  registerButtonBorderColor: '#0d47a1',
  welcomeBadgeText: 'DMA Repository PORTAL',
  welcomeTitleText: 'Welcome to',
  welcomeTitleSecondText: 'DMA Repository Portal',
  welcomeSubtitleText: 'Secure access to municipal administration system',
  govtTitleText: 'Government Of Maharashtra',
  govtSubtitleText: 'Directorate of Municipal Administration',
  formTitleText: 'Login',
  formSubtitleText: 'Sign in with your credentials',
  loginButtonText: 'LOGIN',
  visitorButtonText: 'Visitor Registration',
  showVisitorButton: true,
  showCountdown: true,
  refreshMinutes: 2,
  logoUrl: null,
  logoWidth: '100px',
  customCss: ''
};

// Default Navbar Theme Configuration
const DEFAULT_NAVBAR_THEME = {
  navbarBackground: 'linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)',
  navbarTextColor: '#ffffff',
  navbarButtonBg: 'rgba(255, 255, 255, 0.12)',
  navbarButtonHoverBg: 'rgba(255, 255, 255, 0.2)',
  navbarIconColor: '#ffffff',
  navbarMenuTextColor: '#84bcf5',
  navbarMenuHoverColor: '#ffffff',
  navbarMenuSelectedBg: 'rgba(255, 255, 255, 0.1)',
  navbarAvatarBg: '#1890ff',
  useGradient: true,
  gradientStart: '#020617',
  gradientMid: '#0F172A',
  gradientEnd: '#0F766E',
  solidColor: '#0F172A'
};

// Default Dashboard Config
const DEFAULT_DASHBOARD_CONFIG = {
  heroImages: [],
  showHeroSection: true,
  heroHeight: '600px',
  overlayOpacity: 0.5,
  overlayColor: '#000000',
  autoplaySpeed: 5000,
  showArrows: true,
  showDots: true,
  autoplay: true,
  showFrontPageFilters: true,
  showNormalCardView: true,
  showCompactCardView: true,
  showDataPointView: true,
  showReportCards: true,
  showAnalysisSection: true,
  showJointGraph: true,
  showDivisionWiseAnalysis: true,
  showRankingTable: true,
  showKeyMetricsSummary: true,
  showRowDataTable: true
};

// Default Dashboard Layout
const DEFAULT_DASHBOARD_LAYOUT = [
  { id: 'hero_section', name: 'Hero Section', position_x: 0, position_y: 0, sort_order: 1, is_visible: true },
  { id: 'front_page_filters', name: 'Front Page Filters', position_x: 0, position_y: 1, sort_order: 2, is_visible: true },
  { id: 'compact_cards_view', name: 'Compact Cards View', position_x: 0, position_y: 2, sort_order: 3, is_visible: true },
  { id: 'normal_cards_view', name: 'Normal Cards View', position_x: 0, position_y: 3, sort_order: 4, is_visible: true },
  { id: 'data_point_view', name: 'Data Point View (Left)', position_x: 0, position_y: 4, sort_order: 5, is_visible: true },
  { id: 'report_cards', name: 'Report Cards (Right)', position_x: 1, position_y: 4, sort_order: 6, is_visible: true },
  { id: 'analysis_section', name: 'Analysis Section', position_x: 0, position_y: 5, sort_order: 7, is_visible: true },
  { id: 'joint_graph', name: 'Joint Graph', position_x: 0, position_y: 6, sort_order: 8, is_visible: true },
  { id: 'division_wise_analysis', name: 'Division Wise Analysis', position_x: 0, position_y: 7, sort_order: 9, is_visible: true },
  { id: 'ranking_table', name: 'Ranking Table', position_x: 0, position_y: 8, sort_order: 10, is_visible: true },
  { id: 'key_metrics_summary', name: 'Key Metrics Summary', position_x: 0, position_y: 9, sort_order: 11, is_visible: true },
  { id: 'row_data_table', name: 'Row Data Table', position_x: 0, position_y: 10, sort_order: 12, is_visible: true }
];

// ============================
// DASHBOARD LAYOUT EDITOR COMPONENT (FIXED)
// ============================
const DashboardLayoutEditor = ({ layout, setLayout, onSave, onReset, onBack, saving }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [localLayout, setLocalLayout] = useState(layout || DEFAULT_DASHBOARD_LAYOUT);

  useEffect(() => {
    if (layout && layout.length > 0) {
      setLocalLayout(layout);
    }
  }, [layout]);

  const handleDragStart = (id) => setDraggedItem(id);
  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (targetId) => {
    if (!draggedItem || draggedItem === targetId) return;
    
    const draggedIndex = localLayout.findIndex(p => p.id === draggedItem);
    const targetIndex = localLayout.findIndex(p => p.id === targetId);
    
    const newLayout = [...localLayout];
    const [removed] = newLayout.splice(draggedIndex, 1);
    newLayout.splice(targetIndex, 0, removed);
    
    const updatedLayout = newLayout.map((part, idx) => ({ ...part, sort_order: idx + 1 }));
    setLocalLayout(updatedLayout);
    if (setLayout) setLayout(updatedLayout);
    message.success('Order updated');
  };

  const handlePositionChange = (partId, positionX, positionY) => {
    const updatedLayout = localLayout.map(part => 
      part.id === partId ? { ...part, position_x: positionX, position_y: positionY } : part
    );
    setLocalLayout(updatedLayout);
    if (setLayout) setLayout(updatedLayout);
    message.success('Position updated');
  };

  const handleVisibilityToggle = (partId, isVisible) => {
    const updatedLayout = localLayout.map(part =>
      part.id === partId ? { ...part, is_visible: isVisible } : part
    );
    setLocalLayout(updatedLayout);
    if (setLayout) setLayout(updatedLayout);
  };

  const getPositionLabel = (posX, posY) => {
    const xLabels = { 0: 'Left', 1: 'Right', 2: 'Center' };
    const yLabels = { 0: 'Top', 1: 'Upper', 2: 'Middle', 3: 'Lower', 4: 'Bottom' };
    return `${xLabels[posX] || 'Left'} | ${yLabels[posY] || 'Top'}`;
  };

  const rows = [...new Set(localLayout.map(p => p.position_y))].sort((a, b) => a - b);
  
  const getRowLayout = (rowY) => {
    const rowParts = localLayout.filter(p => p.position_y === rowY);
    const leftPart = rowParts.find(p => p.position_x === 0);
    const rightPart = rowParts.find(p => p.position_x === 1);
    return { leftPart, rightPart };
  };

  const handleSave = () => {
    if (onSave) {
      const layoutToSave = localLayout.map(item => ({
        id: item.id,
        name: item.name,
        position_x: item.position_x,
        position_y: item.position_y,
        width: item.width || 12,
        height: item.height || 6,
        sort_order: item.sort_order,
        is_visible: item.is_visible
      }));
      onSave(layoutToSave);
    }
  };
  
  const handleReset = () => {
    setLocalLayout(DEFAULT_DASHBOARD_LAYOUT);
    if (setLayout) setLayout(DEFAULT_DASHBOARD_LAYOUT);
    if (onReset) onReset();
    message.success('Reset to default');
  };

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh', padding: '20px' }}>
      <div style={{ background: 'white', padding: '12px 20px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back</Button>
          <Title level={4} style={{ margin: 0 }}><LayoutOutlined style={{ marginRight: '8px', color: '#1677ff' }} />Dashboard Layout Editor</Title>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>Save for All Users</Button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', padding: '16px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Text strong>All Dashboard Sections</Text>
          <Text type="secondary" style={{ marginLeft: '12px', fontSize: '12px' }}>Drag to reorder | Configure position & visibility</Text>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {localLayout.sort((a, b) => a.sort_order - b.sort_order).map((part) => (
            <div key={part.id} draggable onDragStart={() => handleDragStart(part.id)} onDragOver={handleDragOver} onDrop={() => handleDrop(part.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0', cursor: 'grab', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <DragOutlined style={{ color: '#999' }} />
                <Text strong style={{ minWidth: '160px' }}>{part.name}</Text>
                <Tag color={part.is_visible ? 'green' : 'default'}>{part.is_visible ? 'Visible' : 'Hidden'}</Tag>
                <Tag color="blue">{getPositionLabel(part.position_x, part.position_y)}</Tag>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Select size="small" value={part.position_x} style={{ width: 85 }} onChange={(v) => handlePositionChange(part.id, v, part.position_y)}>
                  <Select.Option value={0}>Left</Select.Option>
                  <Select.Option value={1}>Right</Select.Option>
                  <Select.Option value={2}>Center</Select.Option>
                </Select>
                <Select size="small" value={part.position_y} style={{ width: 85 }} onChange={(v) => handlePositionChange(part.id, part.position_x, v)}>
                  <Select.Option value={0}>Top</Select.Option>
                  <Select.Option value={1}>Upper</Select.Option>
                  <Select.Option value={2}>Middle</Select.Option>
                  <Select.Option value={3}>Lower</Select.Option>
                  <Select.Option value={4}>Bottom</Select.Option>
                </Select>
                <Switch size="small" checked={part.is_visible} onChange={(checked) => handleVisibilityToggle(part.id, checked)} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '16px', padding: '10px', background: '#f0f5ff', borderRadius: '6px', fontSize: '12px', color: '#666' }}>
          <Text type="secondary">💡 Tips:</Text>
          <div style={{ marginTop: '5px' }}>• Drag & Drop any item to change its order</div>
          <div>• Left/Right/Center - Choose horizontal position</div>
          <div>• Top/Upper/Middle/Lower/Bottom - Choose vertical position (0-4)</div>
          <div>• Show/Hide - Toggle to display or hide sections</div>
          <div>• Click "Save for All Users" to apply changes globally</div>
        </div>
      </div>
    </div>
  );
};

// ============================
// DASHBOARD CONFIG EDITOR COMPONENT
// ============================
const DashboardConfigEditor = ({ config, setConfig, onSave, onReset, onBack, saving }) => {
  const [images, setImages] = useState(config.heroImages || []);
  const [imagesData, setImagesData] = useState(config.heroImagesData || []);
  const [uploading, setUploading] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');

  const handleImageUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload-hero-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      if (response.data.success) {
        const newImages = [...images, response.data.imageUrl];
        const newImagesData = [...imagesData, { id: response.data.imageId, image_url: response.data.imageUrl }];
        setImages(newImages);
        setImagesData(newImagesData);
        setConfig({ ...config, heroImages: newImages, heroImagesData: newImagesData });
        message.success('Image uploaded successfully!');
      }
    } catch (error) {
      message.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddImageByUrl = async () => {
    if (!imageUrlInput.trim()) {
      message.error('Please enter an image URL');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/hero-images`, {
        image_url: imageUrlInput,
        title: '',
        caption: '',
        sort_order: images.length
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        const newImages = [...images, imageUrlInput];
        const newImagesData = [...imagesData, { id: response.data.imageId, image_url: imageUrlInput }];
        setImages(newImages);
        setImagesData(newImagesData);
        setConfig({ ...config, heroImages: newImages, heroImagesData: newImagesData });
        setImageUrlInput('');
        message.success('Image added from URL!');
      }
    } catch (error) {
      message.error('Failed to add image');
    }
  };

  const handleRemoveImage = async (imageId, index) => {
    if (!imageId) {
      const newImages = images.filter((_, i) => i !== index);
      const newImagesData = imagesData.filter((_, i) => i !== index);
      setImages(newImages);
      setImagesData(newImagesData);
      setConfig({ ...config, heroImages: newImages, heroImagesData: newImagesData });
      message.success('Image removed');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/admin/hero-image/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newImages = images.filter((_, i) => i !== index);
      const newImagesData = imagesData.filter((_, i) => i !== index);
      setImages(newImages);
      setImagesData(newImagesData);
      setConfig({ ...config, heroImages: newImages, heroImagesData: newImagesData });
      message.success('Image deleted');
    } catch (error) {
      message.error('Failed to delete image');
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      handleImageUpload(file);
      return false;
    },
    showUploadList: false
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{
        background: 'white', padding: '16px 24px', borderRadius: '12px', marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3} style={{ margin: 0 }}>Dashboard Editor</Title>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button icon={<ReloadOutlined />} onClick={onReset} size="large">Reset to Default</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving} size="large">Save for ALL Users</Button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '24px' }}>
        <Tabs defaultActiveKey="hero" size="large">
          <TabPane tab={<span><PictureOutlined /> Hero Images</span>} key="hero">
            <Card title="Add Images to Hero Section" size="small">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Title level={5}>📤 Upload from Computer</Title>
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />} loading={uploading}>Upload Image</Button>
                  </Upload>
                </div>
                <Divider />
                <div>
                  <Title level={5}>🔗 Add Image by URL</Title>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Input placeholder="https://example.com/image.jpg" value={imageUrlInput} onChange={(e) => setImageUrlInput(e.target.value)} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddImageByUrl}>Add</Button>
                  </div>
                </div>
                <Divider />
                <div>
                  <Title level={5}>🖼️ Images Gallery ({images.length})</Title>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                    {images.map((url, idx) => (
                      <div key={idx} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '8px' }}>
                        <img src={url} alt={`Hero ${idx + 1}`} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                        <Button size="small" danger onClick={() => handleRemoveImage(imagesData[idx]?.id, idx)} style={{ marginTop: '8px', width: '100%' }}>Delete</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </Space>
            </Card>
          </TabPane>

          <TabPane tab={<span><SettingOutlined /> Carousel Settings</span>} key="carousel">
            <Card title="Slideshow Settings" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Auto-play: </label><Switch checked={config.autoplay} onChange={(checked) => setConfig({ ...config, autoplay: checked })} /></div>
                {config.autoplay && (
                  <div><label>Speed (ms): </label><Input type="number" value={config.autoplaySpeed} onChange={(e) => setConfig({ ...config, autoplaySpeed: parseInt(e.target.value) || 5000 })} style={{ width: '150px' }} /></div>
                )}
                <div><label>Show Arrows: </label><Switch checked={config.showArrows} onChange={(checked) => setConfig({ ...config, showArrows: checked })} /></div>
                <div><label>Show Dots: </label><Switch checked={config.showDots} onChange={(checked) => setConfig({ ...config, showDots: checked })} /></div>
              </Space>
            </Card>
          </TabPane>

          <TabPane tab={<span><DashboardOutlined /> Dashboard Parts</span>} key="parts">
            <Card title="Dashboard Sections - Show/Hide" size="small">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Hero Section</span>
                      <Switch checked={config.showHeroSection !== false} onChange={(checked) => setConfig({ ...config, showHeroSection: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Front Page Filters</span>
                      <Switch checked={config.showFrontPageFilters !== false} onChange={(checked) => setConfig({ ...config, showFrontPageFilters: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Normal Cards View</span>
                      <Switch checked={config.showNormalCardView !== false} onChange={(checked) => setConfig({ ...config, showNormalCardView: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Compact Cards View</span>
                      <Switch checked={config.showCompactCardView !== false} onChange={(checked) => setConfig({ ...config, showCompactCardView: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Data Point View</span>
                      <Switch checked={config.showDataPointView !== false} onChange={(checked) => setConfig({ ...config, showDataPointView: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Report Cards (KPI)</span>
                      <Switch checked={config.showReportCards !== false} onChange={(checked) => setConfig({ ...config, showReportCards: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Analysis Section</span>
                      <Switch checked={config.showAnalysisSection !== false} onChange={(checked) => setConfig({ ...config, showAnalysisSection: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Joint Graph</span>
                      <Switch checked={config.showJointGraph !== false} onChange={(checked) => setConfig({ ...config, showJointGraph: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Division Wise Analysis</span>
                      <Switch checked={config.showDivisionWiseAnalysis !== false} onChange={(checked) => setConfig({ ...config, showDivisionWiseAnalysis: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Ranking Table</span>
                      <Switch checked={config.showRankingTable !== false} onChange={(checked) => setConfig({ ...config, showRankingTable: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Key Metrics Summary</span>
                      <Switch checked={config.showKeyMetricsSummary !== false} onChange={(checked) => setConfig({ ...config, showKeyMetricsSummary: checked })} />
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Row Data Table</span>
                      <Switch checked={config.showRowDataTable !== false} onChange={(checked) => setConfig({ ...config, showRowDataTable: checked })} />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

// ============================
// SIDEBAR MENU EDITOR COMPONENT
// ============================
const SidebarMenuEditor = ({ onBack }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleMenuMap, setRoleMenuMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [tempRoleMenuMap, setTempRoleMenuMap] = useState({});
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: '', display_name: '', sort_order: 0, menu_ids: [] });
  const [activeTab, setActiveTab] = useState('role_access');

  const filterOutGraphsMenu = (menus) => {
    const filterMenus = (items) => {
      return items
        .filter(item => item.title !== 'Graphs' && item.title !== 'Graph')
        .map(item => ({
          ...item,
          children: item.children ? filterMenus(item.children) : []
        }));
    };
    return filterMenus(menus);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const menusResponse = await axios.get(`${API_BASE_URL}/api/admin/sidebar-menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (menusResponse.data.success) {
        const filteredMenus = filterOutGraphsMenu(menusResponse.data.menus);
        setMenuItems(filteredMenus);
        const allGroups = menusResponse.data.groups || [];
        setGroups(allGroups);
      }
      const accessResponse = await axios.get(`${API_BASE_URL}/api/admin/role-menu-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (accessResponse.data.success) {
        const filteredRoles = accessResponse.data.roles.filter(
          role => role !== 'CADER' && role !== 'VISITOR' && role !== 'cader' && role !== 'visitor'
        );
        setRoles(filteredRoles);
        const filteredRoleMenuMap = {};
        Object.keys(accessResponse.data.roleMenuMap).forEach(role => {
          if (role !== 'CADER' && role !== 'VISITOR' && role !== 'cader' && role !== 'visitor') {
            filteredRoleMenuMap[role] = accessResponse.data.roleMenuMap[role];
          }
        });
        setRoleMenuMap(filteredRoleMenuMap);
        setTempRoleMenuMap(filteredRoleMenuMap);
        if (filteredRoles.length > 0) setSelectedRole(filteredRoles[0]);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleRoleSelect = (role) => setSelectedRole(role);
  const handleAccessCheckboxChange = (menuId, checked) => {
    if (!selectedRole) return;
    setTempRoleMenuMap(prev => {
      const newMap = { ...prev };
      const currentSet = new Set(prev[selectedRole] || []);
      if (checked) currentSet.add(menuId);
      else currentSet.delete(menuId);
      newMap[selectedRole] = Array.from(currentSet);
      return newMap;
    });
  };
  const handleSelectAllForRole = (checked) => {
    if (!selectedRole) return;
    const getAllMenuIds = (menus) => {
      let ids = [];
      for (const menu of menus) {
        ids.push(menu.id);
        if (menu.children && menu.children.length) ids = ids.concat(getAllMenuIds(menu.children));
      }
      return ids;
    };
    const allMenuIds = getAllMenuIds(menuItems);
    setTempRoleMenuMap(prev => ({ ...prev, [selectedRole]: checked ? allMenuIds : [] }));
  };
  const handleSaveRoleAccess = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const menuIds = tempRoleMenuMap[selectedRole] || [];
      await axios.post(`${API_BASE_URL}/api/admin/role-menu-access`,
        { role_name: selectedRole, menu_ids: menuIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Menu access saved for ${selectedRole}`);
      setRoleMenuMap(prev => ({ ...prev, [selectedRole]: menuIds }));
    } catch (error) {
      message.error('Failed to save menu access');
    } finally {
      setSaving(false);
    }
  };
  const handleAddGroup = async () => {
    if (!newGroup.name || !newGroup.display_name) {
      message.error('Group name and display name required');
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/sidebar-group`, newGroup, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Group added successfully');
      setShowAddGroup(false);
      setEditingGroup(null);
      setNewGroup({ name: '', display_name: '', sort_order: 0, menu_ids: [] });
      loadData();
    } catch (error) {
      message.error('Failed to add group');
    } finally {
      setSaving(false);
    }
  };
  const handleUpdateGroup = async () => {
    if (!newGroup.name || !newGroup.display_name) {
      message.error('Group name and display name required');
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/admin/sidebar-group/${editingGroup.id}`, newGroup, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Group updated successfully');
      setShowAddGroup(false);
      setEditingGroup(null);
      setNewGroup({ name: '', display_name: '', sort_order: 0, menu_ids: [] });
      loadData();
    } catch (error) {
      message.error('Failed to update group');
    } finally {
      setSaving(false);
    }
  };
  const handleDeleteGroup = async (groupId) => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/admin/sidebar-group/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Group deleted');
      loadData();
    } catch (error) {
      message.error('Failed to delete group');
    } finally {
      setSaving(false);
    }
  };
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setNewGroup({
      name: group.name,
      display_name: group.display_name,
      sort_order: group.sort_order,
      menu_ids: group.menu_ids || []
    });
    setShowAddGroup(true);
  };
  const handleGroupMenuSelection = (menuId, checked) => {
    setNewGroup(prev => ({
      ...prev,
      menu_ids: checked ? [...prev.menu_ids, menuId] : prev.menu_ids.filter(id => id !== menuId)
    }));
  };
  const flattenMenus = (menus, level = 0) => {
    let result = [];
    for (const menu of menus) {
      if (menu.title === 'Graphs' || menu.title === 'Graph') continue;
      result.push({ ...menu, level });
      if (menu.children && menu.children.length) result = result.concat(flattenMenus(menu.children, level + 1));
    }
    return result;
  };
  const flatMenus = flattenMenus(menuItems);
  const currentRoleMenuIds = selectedRole ? (tempRoleMenuMap[selectedRole] || []) : [];
  const getRoleColor = (role) => {
    const colors = {
      'SUPER_ADMIN': '#f5222d', 'DMA_ADMIN': '#722ed1', 'DMA_DESK_OFFICER': '#13c2c2',
      'DISTRICT_ADMIN': '#faad14', 'DIVISIONAL_OFFICER': '#1890ff', 'DMA_DESK_USER': '#2db7f5',
      'ULB_CHIEF_OFFICIER': '#52c41a', 'ULB': '#52c41a', 'ULB_OFFICER': '#389e0d'
    };
    return colors[role] || '#1677ff';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{ background: 'white', padding: '16px 24px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3}><MenuOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />Menu Manager</Title>
        </div>
        <Button icon={<PlusOutlined />} onClick={() => { setEditingGroup(null); setNewGroup({ name: '', display_name: '', sort_order: 0, menu_ids: [] }); setShowAddGroup(true); }}>Create New Group</Button>
      </div>
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="👥 Role Menu Access" key="role_access">
            <Row gutter={24}>
              <Col span={6}>
                <Card title="Select Role" size="small">
                  {roles.map(role => (
                    <div key={role} onClick={() => handleRoleSelect(role)} style={{
                      padding: '12px 16px', background: selectedRole === role ? '#e6f7ff' : '#fafafa',
                      borderRadius: '8px', cursor: 'pointer', border: selectedRole === role ? '2px solid #0d47a1' : '1px solid #e8e8e8',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '16px', background: getRoleColor(role), color: 'white', fontSize: '12px', fontWeight: 'bold' }}>{role}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>Menus: {(tempRoleMenuMap[role] || []).length} selected</div>
                    </div>
                  ))}
                </Card>
              </Col>
              <Col span={18}>
                <Card title={`Menu Access for: ${selectedRole || 'Select Role'}`} extra={selectedRole && (
                  <Space>
                    <Button size="small" onClick={() => handleSelectAllForRole(true)}>Select All</Button>
                    <Button size="small" onClick={() => handleSelectAllForRole(false)}>Clear All</Button>
                    <Button type="primary" size="small" onClick={handleSaveRoleAccess} loading={saving}>Save for {selectedRole}</Button>
                  </Space>
                )}>
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e8e8e8' }}><th style={{ padding: '12px', textAlign: 'left' }}>Menu Item</th><th style={{ padding: '12px', textAlign: 'center', width: '100px' }}>Access</th></tr></thead>
                      <tbody>
                        {flatMenus.map(menu => (
                          <tr key={menu.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ paddingLeft: `${menu.level * 20}px` }}>
                                {menu.level > 0 && <span style={{ marginRight: '8px', color: '#999' }}>↳</span>}
                                <span>{menu.title}</span>
                                {menu.route && <div style={{ fontSize: '11px', color: '#999' }}>{menu.route}</div>}
                                {menu.group_id && <div style={{ fontSize: '10px', color: '#eb2f96', marginTop: '2px' }}>📁 In Group</div>}
                              </div>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}><Checkbox checked={currentRoleMenuIds.includes(menu.id)} onChange={(e) => handleAccessCheckboxChange(menu.id, e.target.checked)} disabled={!selectedRole} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </Col>
            </Row>
          </TabPane>
          <TabPane tab="📁 Groups (Navbar Dropdowns)" key="groups">
            {groups.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Existing Groups</Title>
                <Row gutter={16}>
                  {groups.map(group => (
                    <Col key={group.id} span={8} style={{ marginBottom: '12px' }}>
                      <Card size="small">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div><Text strong style={{ fontSize: '16px' }}>{group.display_name}</Text><div><Text type="secondary" style={{ fontSize: '12px' }}>ID: {group.name}</Text></div><div><Text type="secondary" style={{ fontSize: '12px' }}>Menus: {(group.menu_ids || []).length} items</Text></div></div>
                          <Space><Button size="small" icon={<EditOutlined />} onClick={() => handleEditGroup(group)}>Edit</Button><Popconfirm title="Delete this group?" onConfirm={() => handleDeleteGroup(group.id)}><Button size="small" danger icon={<DeleteOutlined />}>Delete</Button></Popconfirm></Space>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <Divider />
              </div>
            )}
            <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '8px', padding: '12px 16px' }}>
              <Text strong>📌 What are Groups?</Text>
              <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                <li>Groups appear as <Text strong>Dropdown Buttons</Text> in the <Text code>Navbar</Text></li>
                <li>Menus inside a group will be <Text strong>hidden from Sidebar</Text></li>
                <li>Menus NOT in any group will appear in <Text code>Sidebar</Text></li>
                <li>Click <Text strong>"Create New Group"</Text> to add a group and select which menus go inside it</li>
              </ul>
            </div>
          </TabPane>
        </Tabs>
      </Card>
      <Modal title={editingGroup ? "Edit Group" : "Create New Group"} open={showAddGroup} onCancel={() => { setShowAddGroup(false); setEditingGroup(null); setNewGroup({ name: '', display_name: '', sort_order: 0, menu_ids: [] }); }} width={600}
        footer={[<Button key="cancel" onClick={() => { setShowAddGroup(false); setEditingGroup(null); }}>Cancel</Button>, <Button key="save" type="primary" onClick={editingGroup ? handleUpdateGroup : handleAddGroup} loading={saving}>{editingGroup ? 'Update Group' : 'Create Group'}</Button>]}>
        <Form layout="vertical">
          <Form.Item label="Group Name (internal)" required><Input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} placeholder="e.g., reports, users, forms" /></Form.Item>
          <Form.Item label="Display Name (shown in Navbar)" required><Input value={newGroup.display_name} onChange={(e) => setNewGroup({ ...newGroup, display_name: e.target.value })} placeholder="e.g., 📊 Reports & Analytics" /></Form.Item>
          <Form.Item label="Sort Order"><Input type="number" value={newGroup.sort_order} onChange={(e) => setNewGroup({ ...newGroup, sort_order: parseInt(e.target.value) || 0 })} /></Form.Item>
          <Form.Item label="Select Menus to include in this Group">
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
              {flatMenus.map(menu => (
                <div key={menu.id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Checkbox checked={newGroup.menu_ids.includes(menu.id)} onChange={(e) => handleGroupMenuSelection(menu.id, e.target.checked)}>
                    <div style={{ paddingLeft: `${menu.level * 20}px` }}>{menu.level > 0 && <span style={{ marginRight: '8px', color: '#999' }}>↳</span>}<span>{menu.title}</span>{menu.route && <div style={{ fontSize: '11px', color: '#999' }}>{menu.route}</div>}</div>
                  </Checkbox>
                </div>
              ))}
            </div>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>✅ Selected menus will appear in Navbar as dropdown under this group<br />❌ Selected menus will be hidden from Sidebar</Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ============================
// CHANGE PASSWORD COMPONENT
// ============================
const ChangePasswordPage = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleChangePassword = async (values) => {
    const { username, newPassword, confirmPassword } = values;
    if (newPassword !== confirmPassword) {
      message.error('New password and confirm password do not match!');
      return;
    }
    if (newPassword.length < 6) {
      message.error('Password must be at least 6 characters long!');
      return;
    }
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/change-password`, {
        username: username,
        new_password: newPassword,
        confirm_password: confirmPassword
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if (response.data.success) {
        message.success(`Password changed successfully for user: ${username}`);
        form.resetFields();
      } else {
        message.error(response.data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      message.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{ background: 'white', padding: '16px 24px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3} style={{ margin: 0 }}><KeyOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />Change User Password</Title>
        </div>
      </div>
      <div style={{ background: 'white', borderRadius: '12px', padding: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: '600px', margin: '0 auto' }}>
        <Form form={form} layout="vertical" onFinish={handleChangePassword} size="large">
          <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please enter username!' }]}><Input prefix={<UserOutlined />} placeholder="Enter username" style={{ borderRadius: '8px' }} /></Form.Item>
          <Form.Item name="newPassword" label="New Password" rules={[{ required: true, message: 'Please enter new password!' }, { min: 6, message: 'Password must be at least 6 characters!' }]}><Input.Password prefix={<LockOutlined />} placeholder="Enter new password" style={{ borderRadius: '8px' }} /></Form.Item>
          <Form.Item name="confirmPassword" label="Confirm New Password" rules={[{ required: true, message: 'Please confirm new password!' }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('newPassword') === value) return Promise.resolve(); return Promise.reject(new Error('Passwords do not match!')); } })]}><Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" style={{ borderRadius: '8px' }} /></Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" loading={loading} block style={{ background: '#0d47a1', height: '45px', borderRadius: '8px' }}>Change Password</Button></Form.Item>
        </Form>
        <Divider />
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center' }}>Note: This will change password for the specified user immediately.</Text>
      </div>
    </div>
  );
};

// ============================
// LOGIN PAGE THEME EDITOR COMPONENT
// ============================
const ThemeEditorPage = ({ theme, setTheme, onSave, onReset, onBack, saving }) => {
  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{ background: 'white', padding: '16px 24px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3} style={{ margin: 0 }}><EditOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />Login Page Editor</Title>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button icon={<ReloadOutlined />} onClick={onReset} size="large">Reset to Default</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving} size="large" style={{ background: '#0d47a1' }}>Save for ALL Users</Button>
        </div>
      </div>
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Tabs defaultActiveKey="colors" size="large">
          <TabPane tab={<span><BgColorsOutlined /> Colors & Backgrounds</span>} key="colors">
            <Card title="Page Background" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Background Type: </label><select value={theme.pageBg.type} onChange={(e) => setTheme({ ...theme, pageBg: { ...theme.pageBg, type: e.target.value } })} style={{ marginLeft: 10, padding: '4px 8px' }}><option value="gradient">Gradient</option><option value="solid">Solid Color</option></select></div>
                {theme.pageBg.type === 'gradient' ? (<><div><label>Gradient Start: </label><ColorPicker value={theme.pageBg.gradientStart} onChange={(color) => setTheme({ ...theme, pageBg: { ...theme.pageBg, gradientStart: color.toHexString() } })} /></div><div><label>Gradient End: </label><ColorPicker value={theme.pageBg.gradientEnd} onChange={(color) => setTheme({ ...theme, pageBg: { ...theme.pageBg, gradientEnd: color.toHexString() } })} /></div></>) : (<div><label>Solid Color: </label><ColorPicker value={theme.pageBg.solidColor} onChange={(color) => setTheme({ ...theme, pageBg: { ...theme.pageBg, solidColor: color.toHexString() } })} /></div>)}
              </Space>
            </Card>
            <Card title="Image Section Background" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Background Type: </label><select value={theme.imageSectionBg.type} onChange={(e) => setTheme({ ...theme, imageSectionBg: { ...theme.imageSectionBg, type: e.target.value } })} style={{ marginLeft: 10, padding: '4px 8px' }}><option value="gradient">Gradient</option><option value="solid">Solid Color</option></select></div>
                {theme.imageSectionBg.type === 'gradient' ? (<><div><label>Gradient Start: </label><ColorPicker value={theme.imageSectionBg.gradientStart} onChange={(color) => setTheme({ ...theme, imageSectionBg: { ...theme.imageSectionBg, gradientStart: color.toHexString() } })} /></div><div><label>Gradient End: </label><ColorPicker value={theme.imageSectionBg.gradientEnd} onChange={(color) => setTheme({ ...theme, imageSectionBg: { ...theme.imageSectionBg, gradientEnd: color.toHexString() } })} /></div></>) : (<div><label>Solid Color: </label><ColorPicker value={theme.imageSectionBg.solidColor} onChange={(color) => setTheme({ ...theme, imageSectionBg: { ...theme.imageSectionBg, solidColor: color.toHexString() } })} /></div>)}
              </Space>
            </Card>
            <Card title="Text Colors" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Welcome Title Color: </label><ColorPicker value={theme.welcomeTitleColor} onChange={(color) => setTheme({ ...theme, welcomeTitleColor: color.toHexString() })} /></div>
                <div><label>Form Title Color: </label><ColorPicker value={theme.formTitleColor} onChange={(color) => setTheme({ ...theme, formTitleColor: color.toHexString() })} /></div>
                <div><label>Button Background: </label><select value={theme.loginButtonBg.type} onChange={(e) => setTheme({ ...theme, loginButtonBg: { ...theme.loginButtonBg, type: e.target.value } })} style={{ marginRight: 10, padding: '4px 8px' }}><option value="gradient">Gradient</option><option value="solid">Solid</option></select>{theme.loginButtonBg.type === 'gradient' ? (<><ColorPicker value={theme.loginButtonBg.gradientStart} onChange={(color) => setTheme({ ...theme, loginButtonBg: { ...theme.loginButtonBg, gradientStart: color.toHexString() } })} /><span> to </span><ColorPicker value={theme.loginButtonBg.gradientEnd} onChange={(color) => setTheme({ ...theme, loginButtonBg: { ...theme.loginButtonBg, gradientEnd: color.toHexString() } })} /></>) : (<ColorPicker value={theme.loginButtonBg.solidColor} onChange={(color) => setTheme({ ...theme, loginButtonBg: { ...theme.loginButtonBg, solidColor: color.toHexString() } })} />)}</div>
              </Space>
            </Card>
          </TabPane>
          <TabPane tab={<span><FormOutlined /> Text Content</span>} key="text">
            <Card title="Welcome Section Text" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}><div><label>Badge Text: </label><Input value={theme.welcomeBadgeText} onChange={(e) => setTheme({ ...theme, welcomeBadgeText: e.target.value })} /></div><div><label>Welcome Title Line 1: </label><Input value={theme.welcomeTitleText} onChange={(e) => setTheme({ ...theme, welcomeTitleText: e.target.value })} /></div><div><label>Welcome Title Line 2: </label><Input value={theme.welcomeTitleSecondText} onChange={(e) => setTheme({ ...theme, welcomeTitleSecondText: e.target.value })} /></div><div><label>Subtitle Text: </label><Input.TextArea value={theme.welcomeSubtitleText} onChange={(e) => setTheme({ ...theme, welcomeSubtitleText: e.target.value })} rows={2} /></div></Space>
            </Card>
            <Card title="Government Section Text" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}><div><label>Government Title: </label><Input value={theme.govtTitleText} onChange={(e) => setTheme({ ...theme, govtTitleText: e.target.value })} /></div><div><label>Government Subtitle: </label><Input value={theme.govtSubtitleText} onChange={(e) => setTheme({ ...theme, govtSubtitleText: e.target.value })} /></div></Space>
            </Card>
            <Card title="Form Section Text" size="small">
              <Space direction="vertical" style={{ width: '100%' }}><div><label>Form Title: </label><Input value={theme.formTitleText} onChange={(e) => setTheme({ ...theme, formTitleText: e.target.value })} /></div><div><label>Form Subtitle: </label><Input value={theme.formSubtitleText} onChange={(e) => setTheme({ ...theme, formSubtitleText: e.target.value })} /></div><div><label>Login Button Text: </label><Input value={theme.loginButtonText} onChange={(e) => setTheme({ ...theme, loginButtonText: e.target.value })} /></div><div><label>Visitor Button Text: </label><Input value={theme.visitorButtonText} onChange={(e) => setTheme({ ...theme, visitorButtonText: e.target.value })} /></div></Space>
            </Card>
          </TabPane>
          <TabPane tab={<span><SettingOutlined /> Settings</span>} key="settings">
            <Card title="UI Settings" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Show Visitor Button: </label><Switch checked={theme.showVisitorButton} onChange={(checked) => setTheme({ ...theme, showVisitorButton: checked })} /></div>
                <Divider />
                <div><label>Show Countdown Timer: </label><Switch checked={theme.showCountdown} onChange={(checked) => setTheme({ ...theme, showCountdown: checked })} /></div>
                <div><label>Auto-refresh Minutes: </label><Input type="number" value={theme.refreshMinutes} onChange={(e) => setTheme({ ...theme, refreshMinutes: parseInt(e.target.value) || 2 })} style={{ width: '100px' }} /><small style={{ marginLeft: 10 }}>Set 0 to disable auto-refresh</small></div>
              </Space>
            </Card>
            <Card title="Logo Settings" size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {theme.logoUrl && (<div style={{ textAlign: 'center', padding: 16, background: '#f5f5f5', borderRadius: 8, marginBottom: 8 }}><div style={{ marginBottom: 8, fontWeight: 500 }}>Current Logo:</div><img src={theme.logoUrl} alt="Logo Preview" style={{ maxWidth: '150px', maxHeight: '80px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '4px', background: 'white' }} /><Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setTheme({ ...theme, logoUrl: null }); message.success('Logo URL cleared'); }} style={{ marginTop: 8 }}>Remove Logo</Button></div>)}
                <div><div style={{ marginBottom: 8, fontWeight: 500 }}>📤 Upload from Computer</div><Upload beforeUpload={(file) => { const isImage = file.type.startsWith('image/'); if (!isImage) { message.error('You can only upload image files!'); return false; } const isLt2M = file.size / 1024 / 1024 < 2; if (!isLt2M) { message.error('Logo must be smaller than 2MB!'); return false; } const reader = new FileReader(); reader.onload = (e) => { setTheme({ ...theme, logoUrl: e.target.result }); message.success('Logo uploaded successfully!'); }; reader.readAsDataURL(file); return false; }} showUploadList={false}><Button icon={<UploadOutlined />}>Upload Logo Image</Button></Upload><Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>Supported: JPG, PNG, GIF (Max 2MB)</Text></div>
                <Divider style={{ margin: '8px 0' }}><Text type="secondary">OR</Text></Divider>
                <div><div style={{ marginBottom: 8, fontWeight: 500 }}>🔗 Or Enter Image URL</div><Input value={theme.logoUrl || ''} onChange={(e) => setTheme({ ...theme, logoUrl: e.target.value || null })} placeholder="https://example.com/logo.png" /><Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>Enter direct image URL (jpg, png, gif, svg)</Text></div>
                <Divider style={{ margin: '8px 0' }} />
                <div><div style={{ marginBottom: 8, fontWeight: 500 }}>📏 Logo Size</div><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Input value={theme.logoWidth} onChange={(e) => setTheme({ ...theme, logoWidth: e.target.value })} style={{ width: '120px' }} placeholder="100px" /><Select value={theme.logoWidth} onChange={(value) => setTheme({ ...theme, logoWidth: value })} style={{ width: '120px' }} placeholder="Select size"><Option value="60px">Small (60px)</Option><Option value="100px">Medium (100px)</Option><Option value="150px">Large (150px)</Option><Option value="200px">Extra Large (200px)</Option></Select></div><Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '5px' }}>Example: 100px, 150px, 200px, or 20%</Text></div>
              </Space>
            </Card>
            <Card title="Custom CSS" size="small" style={{ marginTop: 16 }}><Input.TextArea value={theme.customCss} onChange={(e) => setTheme({ ...theme, customCss: e.target.value })} rows={6} placeholder="/* Add your custom CSS here */" style={{ fontFamily: 'monospace' }} /></Card>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

// ============================
// NAVBAR THEME EDITOR COMPONENT (WITH TITLE TAB)
// ============================
const NavbarThemeEditor = ({ navbarTheme, setNavbarTheme, navbarTitle, setNavbarTitle, onSave, onReset, onBack, saving }) => {
  const [localTitle, setLocalTitle] = useState(navbarTitle || 'DMA Repository Portal');

  useEffect(() => {
    setLocalTitle(navbarTitle || 'DMA Repository Portal');
  }, [navbarTitle]);

  const handleSave = () => {
    onSave(navbarTheme, localTitle);
  };

  const handleReset = () => {
    onReset();
    setLocalTitle('DMA Repository Portal');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{ background: 'white', padding: '16px 24px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3} style={{ margin: 0 }}><LayoutOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />Navbar Theme & Title Editor</Title>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset} size="large">Reset to Default</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} size="large" style={{ background: '#0d47a1' }}>Save for ALL Users</Button>
        </div>
      </div>
      
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Tabs defaultActiveKey="title" size="large">
          <TabPane tab={<span><EditOutlined /> Navbar Title</span>} key="title">
            <Card title="Navbar Title Settings" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Portal Title <span style={{ color: '#e53e3e' }}>*</span>
                  </label>
                  <Input 
                    value={localTitle} 
                    onChange={(e) => setLocalTitle(e.target.value)}
                    placeholder="Enter portal title"
                    size="large"
                    style={{ borderRadius: '8px' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    This title will appear in the navbar header
                  </Text>
                </div>
                
                <Divider />
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Live Preview
                  </label>
                  <div style={{
                    background: navbarTheme.navbarBackground || 'linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{
                      color: navbarTheme.navbarTextColor || '#ffffff',
                      fontSize: '24px',
                      fontWeight: 800,
                      textShadow: '0 2px 15px rgba(0, 0, 0, 0.5)',
                      background: 'linear-gradient(135deg, #ffffff, #f0f0f0, #ffffff)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      letterSpacing: '1px'
                    }}>
                      {localTitle || 'DMA Repository Portal'}
                    </span>
                  </div>
                </div>
              </Space>
            </Card>
          </TabPane>
          
          <TabPane tab={<span><LayoutOutlined /> Navbar Background</span>} key="navbar">
            <Card title="Navbar Background Settings" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><label>Background Type: </label><select value={navbarTheme.useGradient ? 'gradient' : 'solid'} onChange={(e) => setNavbarTheme({ ...navbarTheme, useGradient: e.target.value === 'gradient' })} style={{ marginLeft: 10, padding: '4px 8px' }}><option value="gradient">Gradient</option><option value="solid">Solid Color</option></select></div>
                {navbarTheme.useGradient ? (<><div><label>Gradient Start: </label><ColorPicker value={navbarTheme.gradientStart} onChange={(color) => setNavbarTheme({ ...navbarTheme, gradientStart: color.toHexString(), navbarBackground: `linear-gradient(90deg, ${color.toHexString()} 0%, ${navbarTheme.gradientMid} 40%, ${navbarTheme.gradientEnd} 100%)` })} /></div><div><label>Gradient Middle: </label><ColorPicker value={navbarTheme.gradientMid} onChange={(color) => setNavbarTheme({ ...navbarTheme, gradientMid: color.toHexString(), navbarBackground: `linear-gradient(90deg, ${navbarTheme.gradientStart} 0%, ${color.toHexString()} 40%, ${navbarTheme.gradientEnd} 100%)` })} /></div><div><label>Gradient End: </label><ColorPicker value={navbarTheme.gradientEnd} onChange={(color) => setNavbarTheme({ ...navbarTheme, gradientEnd: color.toHexString(), navbarBackground: `linear-gradient(90deg, ${navbarTheme.gradientStart} 0%, ${navbarTheme.gradientMid} 40%, ${color.toHexString()} 100%)` })} /></div></>) : (<div><label>Solid Color: </label><ColorPicker value={navbarTheme.solidColor} onChange={(color) => setNavbarTheme({ ...navbarTheme, solidColor: color.toHexString(), navbarBackground: color.toHexString() })} /></div>)}
              </Space>
            </Card>
            
            <Card title="Text & Icon Colors" size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}><div><label>Navbar Text Color: </label><ColorPicker value={navbarTheme.navbarTextColor} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarTextColor: color.toHexString() })} /></div><div><label>Navbar Icon Color: </label><ColorPicker value={navbarTheme.navbarIconColor} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarIconColor: color.toHexString() })} /></div><div><label>Menu Text Color: </label><ColorPicker value={navbarTheme.navbarMenuTextColor} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarMenuTextColor: color.toHexString() })} /></div><div><label>Menu Hover Color: </label><ColorPicker value={navbarTheme.navbarMenuHoverColor} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarMenuHoverColor: color.toHexString() })} /></div></Space>
            </Card>
            
            <Card title="Button & Avatar Colors" size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}><div><label>Button Background: </label><ColorPicker value={navbarTheme.navbarButtonBg} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarButtonBg: color.toHexString() + '20' })} /></div><div><label>Button Hover Background: </label><ColorPicker value={navbarTheme.navbarButtonHoverBg} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarButtonHoverBg: color.toHexString() + '33' })} /></div><div><label>Menu Selected Background: </label><ColorPicker value={navbarTheme.navbarMenuSelectedBg} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarMenuSelectedBg: color.toHexString() + '1a' })} /></div><div><label>Avatar Background: </label><ColorPicker value={navbarTheme.navbarAvatarBg} onChange={(color) => setNavbarTheme({ ...navbarTheme, navbarAvatarBg: color.toHexString() })} /></div></Space>
            </Card>
          </TabPane>
        </Tabs>
        
        <Card title="Live Preview" size="small" style={{ marginTop: 16 }}>
          <div style={{ background: navbarTheme.navbarBackground, padding: '16px 24px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: navbarTheme.navbarButtonBg, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: navbarTheme.navbarIconColor }}>☰</span>
              </div>
              <span style={{ color: navbarTheme.navbarTextColor, fontWeight: 'bold', fontSize: '20px' }}>
                {localTitle || 'DMA Repository Portal'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ padding: '6px 12px', background: navbarTheme.navbarButtonBg, borderRadius: '6px', color: navbarTheme.navbarTextColor }}>Menu Item 1</div>
              <div style={{ width: '36px', height: '36px', background: navbarTheme.navbarAvatarBg, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>U</div>
            </div>
          </div>
          <Text type="secondary" style={{ display: 'block', marginTop: '12px', textAlign: 'center' }}>This is how your navbar will look after saving</Text>
        </Card>
      </div>
    </div>
  );
};

// ============================
// PROFILE LAYOUT EDITOR COMPONENT
// ============================
const ProfileLayoutEditor = ({ onBack, saving }) => {
  const AVAILABLE_WIDGETS = [
    { id: 'user_avatar', label: 'User Avatar', icon: '👤' },
    { id: 'user_name', label: 'User Name', icon: '📛' },
    { id: 'user_role', label: 'User Role', icon: '🎯' },
    { id: 'user_info', label: 'User Info', icon: '📋' },
    { id: 'change_password', label: 'Change Password', icon: '🔑' },
    { id: 'admin_portal', label: 'Admin Portal', icon: '⚙️' },
    { id: 'sign_out', label: 'Sign Out', icon: '🚪' },
  ];

  const DEFAULT_LAYOUT = [
    { rowId: 'row_0', columns: 1, widgets: ['user_avatar'] },
    { rowId: 'row_1', columns: 3, widgets: ['user_name'] },
    { rowId: 'row_2', columns: 1, widgets: ['user_info'] },
    { rowId: 'row_3', columns: 2, widgets: ['change_password','sign_out' ] },
    { rowId: 'row_4', columns: 1, widgets: ['admin_portal'] },
  ];

  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem('profile_layout');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return DEFAULT_LAYOUT;
      }
    }
    return DEFAULT_LAYOUT;
  });

  const [selectedWidget, setSelectedWidget] = useState(null);

  const getWidgetById = (widgetId) => {
    return AVAILABLE_WIDGETS.find(w => w.id === widgetId);
  };

  const isWidgetPlaced = (widgetId) => {
    return layout.some(row => row.widgets.includes(widgetId));
  };

  const handleAssignWidget = (widgetId, rowId, colIndex) => {
    let newLayout = [...layout];
    
    if (widgetId !== 'empty') {
      for (let r of newLayout) {
        const idx = r.widgets.indexOf(widgetId);
        if (idx !== -1) {
          r.widgets[idx] = null;
        }
      }
    }

    const row = newLayout.find(r => r.rowId === rowId);
    if (row && colIndex < row.widgets.length) {
      row.widgets[colIndex] = widgetId === 'empty' ? null : widgetId;
    }

    setLayout(newLayout);
    setSelectedWidget(null);
    message.success(widgetId === 'empty' ? 'Widget removed!' : 'Widget placed!');
  };

  const updateRowColumns = (rowId, newColumnCount) => {
    const row = layout.find(r => r.rowId === rowId);
    if (!row) return;

    const currentWidgets = row.widgets;
    let newWidgets = [];

    if (newColumnCount > currentWidgets.length) {
      newWidgets = [...currentWidgets];
      for (let i = currentWidgets.length; i < newColumnCount; i++) {
        newWidgets.push(null);
      }
    } else {
      newWidgets = currentWidgets.slice(0, newColumnCount);
    }

    setLayout(layout.map(r => 
      r.rowId === rowId ? { ...r, columns: newColumnCount, widgets: newWidgets } : r
    ));
  };

  const addRow = () => {
    const newRowId = `row_${layout.length}`;
    const newRow = {
      rowId: newRowId,
      columns: 3,
      widgets: [null, null, null]
    };
    setLayout([...layout, newRow]);
  };

  const deleteRow = (rowId) => {
    if (layout.length <= 1) {
      message.warning('Minimum 1 row required');
      return;
    }
    setLayout(layout.filter(row => row.rowId !== rowId));
  };

  const moveRowUp = (index) => {
    if (index === 0) return;
    const newLayout = [...layout];
    [newLayout[index], newLayout[index - 1]] = [newLayout[index - 1], newLayout[index]];
    setLayout(newLayout);
  };

  const moveRowDown = (index) => {
    if (index === layout.length - 1) return;
    const newLayout = [...layout];
    [newLayout[index], newLayout[index + 1]] = [newLayout[index + 1], newLayout[index]];
    setLayout(newLayout);
  };

  const handleSave = async () => {
    localStorage.setItem('profile_layout', JSON.stringify(layout));
    message.success('Profile layout saved successfully!');
    window.dispatchEvent(new Event('profileLayoutUpdated'));
    await saveToDatabase(layout);
  };

  const saveToDatabase = async (layoutData) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return false;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/user/profile-layout`, 
        { layout: layoutData },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (response.data.success) {
        console.log('✅ Layout saved to database');
        return true;
      } else {
        console.error('❌ Failed to save to database:', response.data.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Error saving to database:', error);
      return false;
    }
  };

  const handleReset = () => {
    setLayout(DEFAULT_LAYOUT);
    localStorage.removeItem('profile_layout');
    message.success('Reset to default layout');
  };

  const getColumnLabel = (colIndex) => {
    const labels = ['1st', '2nd', '3rd'];
    return labels[colIndex] || `${colIndex + 1}th`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      <div style={{ 
        background: 'white', 
        padding: '16px 24px', 
        borderRadius: '12px', 
        marginBottom: '20px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">Back</Button>
          <Title level={3} style={{ margin: 0 }}>
            <LayoutOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />
            Profile Layout Editor
          </Title>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} style={{ background: '#0d47a1' }}>
            Save Layout
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flex: '0 0 280px',
          maxHeight: '500px',
          overflowY: 'auto'
        }}>
          <Title level={4}>📦 Available Widgets</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '12px' }}>
            Click a widget → Click a cell in the grid to place it
          </Text>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {AVAILABLE_WIDGETS.map(widget => {
              const isPlaced = isWidgetPlaced(widget.id);
              const isSelected = selectedWidget === widget.id;
              
              return (
                <div
                  key={widget.id}
                  onClick={() => {
                    if (isPlaced) {
                      message.info('This widget is already placed in the grid');
                      return;
                    }
                    setSelectedWidget(widget.id);
                  }}
                  style={{
                    padding: '8px 12px',
                    background: isSelected ? '#e6f7ff' : (isPlaced ? '#f5f5f5' : '#fafafa'),
                    border: isSelected ? '2px solid #0d47a1' : '1px solid #e8e8e8',
                    borderRadius: '8px',
                    cursor: isPlaced ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s',
                    opacity: isPlaced ? 0.5 : 1
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{widget.icon}</span>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: isSelected ? 600 : 400 }}>
                    {widget.label}
                  </span>
                  {isPlaced && <Tag color="green" style={{ fontSize: '10px' }}>Placed</Tag>}
                  {isSelected && <Tag color="blue">Selected</Tag>}
                </div>
              );
            })}
          </div>

          {selectedWidget && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f0f7ff', borderRadius: '8px' }}>
              <Text strong>Selected: </Text>
              <Text>{AVAILABLE_WIDGETS.find(w => w.id === selectedWidget)?.label}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Click a cell in the grid to place this widget
              </Text>
            </div>
          )}
        </div>

        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flex: 1,
          minWidth: '400px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>📐 Layout Preview</Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Rows: {layout.length} | Widgets: {layout.reduce((acc, row) => acc + row.widgets.filter(w => w).length, 0)}
            </Text>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '700px' }}>
            {layout.map((row, rowIndex) => (
              <div key={row.rowId} style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '10px',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text strong style={{ fontSize: '12px' }}>Row {rowIndex + 1}</Text>
                    <Select
                      size="small"
                      value={row.columns}
                      onChange={(value) => updateRowColumns(row.rowId, value)}
                      style={{ width: '100px' }}
                    >
                      <Option value={1}>1 Column</Option>
                      <Option value={2}>2 Columns</Option>
                      <Option value={3}>3 Columns</Option>
                    </Select>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Button size="small" icon={<UpOutlined />} onClick={() => moveRowUp(rowIndex)} disabled={rowIndex === 0} />
                    <Button size="small" icon={<DownOutlined />} onClick={() => moveRowDown(rowIndex)} disabled={rowIndex === layout.length - 1} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteRow(row.rowId)} disabled={layout.length <= 1} />
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${row.columns}, 1fr)`,
                  gap: '8px'
                }}>
                  {Array.from({ length: row.columns }).map((_, colIndex) => {
                    const widgetId = row.widgets[colIndex] || null;
                    const widget = widgetId ? AVAILABLE_WIDGETS.find(w => w.id === widgetId) : null;

                    return (
                      <div
                        key={`${row.rowId}_${colIndex}`}
                        onClick={() => {
                          if (selectedWidget) {
                            handleAssignWidget(selectedWidget, row.rowId, colIndex);
                          } else if (widget) {
                            handleAssignWidget('empty', row.rowId, colIndex);
                          }
                        }}
                        style={{
                          minHeight: '70px',
                          background: widget ? '#f0f7ff' : '#fafafa',
                          border: widget ? '2px solid #0d47a1' : '2px dashed #d9d9d9',
                          borderRadius: '8px',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: selectedWidget ? 'pointer' : (widget ? 'pointer' : 'default'),
                          transition: 'all 0.2s',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        {widget ? (
                          <>
                            <div style={{ fontSize: '28px' }}>{widget.icon}</div>
                            <div style={{ fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>
                              {widget.label}
                            </div>
                            {!selectedWidget && (
                              <div style={{
                                position: 'absolute',
                                top: '2px',
                                right: '4px',
                                fontSize: '10px',
                                color: '#ccc',
                                cursor: 'pointer'
                              }}>
                                ✕
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '18px', color: '#ccc' }}>+</div>
                            <div style={{ fontSize: '9px', color: '#bbb', marginTop: '2px' }}>
                              {getColumnLabel(colIndex)}
                            </div>
                            {selectedWidget && (
                              <div style={{ fontSize: '8px', color: '#0d47a1', marginTop: '2px' }}>
                                Click to place
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '16px' }}>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={addRow}
              style={{ width: '100%', height: '40px', borderRadius: '8px' }}
            >
              + Add Row
            </Button>
          </div>

          <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
            <Text strong>How to use:</Text>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap', fontSize: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: '#f0f7ff', border: '2px solid #0d47a1', borderRadius: '4px' }}></div>
                <span>Widget placed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: '#fafafa', border: '2px dashed #d9d9d9', borderRadius: '4px' }}></div>
                <span>Empty cell</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Tag color="blue">1. Select a widget</Tag>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Tag color="green">2. Click a cell</Tag>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <Card title="📊 Layout Summary" size="small">
          <Row gutter={[8, 8]}>
            {layout.map((row, rowIndex) => (
              <Col span={24} key={row.rowId}>
                <div style={{ 
                  padding: '6px 12px', 
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Text strong>Row {rowIndex + 1}:</Text>
                  <Text type="secondary">({row.columns} column{row.columns > 1 ? 's' : ''})</Text>
                  {row.widgets.map((widgetId, idx) => {
                    const widget = getWidgetById(widgetId);
                    return (
                      <Tag key={idx} color={widget ? 'blue' : 'default'} style={{ margin: 0 }}>
                        {widget ? `${widget.icon} ${widget.label}` : 'Empty'}
                      </Tag>
                    );
                  })}
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      </div>
    </div>
  );
};

// ============================
// MAIN ADMIN PORTAL COMPONENT
// ============================
const AdminPortal = ({ visible, onClose, onThemeSave, currentTheme }) => {
  const [theme, setTheme] = useState(currentTheme || DEFAULT_THEME);
  const [navbarTheme, setNavbarTheme] = useState(DEFAULT_NAVBAR_THEME);
  const [dashboardConfig, setDashboardConfig] = useState(DEFAULT_DASHBOARD_CONFIG);
  const [dashboardLayout, setDashboardLayout] = useState(DEFAULT_DASHBOARD_LAYOUT);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showNavbarEditor, setShowNavbarEditor] = useState(false);
  const [showSidebarEditor, setShowSidebarEditor] = useState(false);
  const [showDashboardEditor, setShowDashboardEditor] = useState(false);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const [showProfileLayoutEditor, setShowProfileLayoutEditor] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false); // NEW
  const [navbarTitle, setNavbarTitle] = useState('DMA Repository Portal');

  useEffect(() => {
    if (currentTheme) setTheme(currentTheme);
    loadDashboardConfig();
    loadDashboardLayout();
    loadNavbarTitle(); 
  }, [currentTheme]);

  const loadDashboardConfig = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDashboardConfig(response.data.dashboardConfig);
      }
    } catch (error) {
      console.error('Error loading dashboard config:', error);
    }
  };

  const loadDashboardLayout = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/dashboard-layout`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.layout) {
        setDashboardLayout(response.data.layout);
      }
    } catch (error) {
      console.error('Error loading dashboard layout:', error);
    }
  };

  const loadNavbarTitle = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/navbar-title`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data.success) {
        setNavbarTitle(response.data.navbarTitle);
        sessionStorage.setItem('navbarTitle', response.data.navbarTitle);
      }
    } catch (error) {
      console.error('Error loading navbar title:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/theme`, { theme: theme }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.data.success) {
        message.success('Login page theme saved for ALL users! 🎨');
        if (onThemeSave) onThemeSave(theme);
        setShowEditor(false);
        onClose();
      } else {
        message.error('Failed to save theme');
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      message.error(error.response?.status === 403 ? 'Admin access required' : 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNavbarTheme = async (theme, title) => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      
      sessionStorage.setItem('navbarTheme', JSON.stringify(theme || navbarTheme));
      const themeResponse = await axios.post(`${API_BASE_URL}/api/admin/navbar-theme`, 
        { navbarTheme: theme || navbarTheme }, 
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      if (title) {
        const titleResponse = await axios.post(`${API_BASE_URL}/api/admin/navbar-title`, 
          { title },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (titleResponse.data.success) {
          setNavbarTitle(title);
          sessionStorage.setItem('navbarTitle', title);
        }
      }
      
      if (themeResponse.data.success) {
        message.success('Navbar theme and title saved for ALL users! 🎨');
        setShowNavbarEditor(false);
      } else {
        message.error('Failed to save navbar theme');
      }
    } catch (error) {
      console.error('Error saving navbar theme:', error);
      message.error('Failed to save navbar theme');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDashboardConfig = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/dashboard-config`, dashboardConfig, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.data.success) {
        message.success('Dashboard configuration saved');
        setShowDashboardEditor(false);
      } else {
        message.error('Failed to save dashboard config');
      }
    } catch (error) {
      console.error('Error saving dashboard config:', error);
      message.error('Failed to save dashboard config');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDashboardLayout = async (layout) => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/dashboard-layout`, { layout }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.data.success) {
        message.success('Dashboard layout saved for ALL users!');
        setDashboardLayout(layout);
        setShowLayoutEditor(false);
      } else {
        message.error('Failed to save layout');
      }
    } catch (error) {
      console.error('Error saving layout:', error);
      message.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDashboardLayout = () => {
    setDashboardLayout(DEFAULT_DASHBOARD_LAYOUT);
    message.success('Layout reset to default');
  };

  const handleReset = () => setTheme(DEFAULT_THEME);
  const handleResetNavbar = () => setNavbarTheme(DEFAULT_NAVBAR_THEME);
  const handleResetDashboard = () => setDashboardConfig(DEFAULT_DASHBOARD_CONFIG);

  const openEditor = () => setShowEditor(true);
  const openChangePassword = () => setShowChangePassword(true);
  const openNavbarEditor = () => setShowNavbarEditor(true);
  const openSidebarEditor = () => setShowSidebarEditor(true);
  const openDashboardEditor = () => setShowDashboardEditor(true);
  const openLayoutEditor = () => setShowLayoutEditor(true);
  const openProfileLayoutEditor = () => setShowProfileLayoutEditor(true);
  const openRoleManager = () => setShowRoleManager(true); // NEW

  const closeEditor = () => setShowEditor(false);
  const closeChangePassword = () => setShowChangePassword(false);
  const closeNavbarEditor = () => setShowNavbarEditor(false);
  const closeSidebarEditor = () => setShowSidebarEditor(false);
  const closeDashboardEditor = () => setShowDashboardEditor(false);
  const closeLayoutEditor = () => setShowLayoutEditor(false);
  const closeProfileLayoutEditor = () => setShowProfileLayoutEditor(false);
  const closeRoleManager = () => setShowRoleManager(false); // NEW

  // ===== ROLE MANAGER ROUTE =====
  if (showRoleManager) return <RoleManager onBack={closeRoleManager} />;

  // ===== OTHER EDITOR ROUTES =====
  if (showChangePassword) return <ChangePasswordPage onBack={closeChangePassword} />;
  if (showEditor) return <ThemeEditorPage theme={theme} setTheme={setTheme} onSave={handleSave} onReset={handleReset} onBack={closeEditor} saving={saving} />;
  if (showNavbarEditor) return (
    <NavbarThemeEditor 
      navbarTheme={navbarTheme} 
      setNavbarTheme={setNavbarTheme} 
      navbarTitle={navbarTitle}
      setNavbarTitle={setNavbarTitle}
      onSave={handleSaveNavbarTheme} 
      onReset={handleResetNavbar} 
      onBack={closeNavbarEditor} 
      saving={saving} 
    />
  );
  if (showSidebarEditor) return <SidebarMenuEditor onBack={closeSidebarEditor} />;
  if (showDashboardEditor) return <DashboardConfigEditor config={dashboardConfig} setConfig={setDashboardConfig} onSave={handleSaveDashboardConfig} onReset={handleResetDashboard} onBack={closeDashboardEditor} saving={saving} />;
  if (showLayoutEditor) return <DashboardLayoutEditor layout={dashboardLayout} setLayout={setDashboardLayout} onSave={handleSaveDashboardLayout} onReset={handleResetDashboardLayout} onBack={closeLayoutEditor} saving={saving} />;
  if (showProfileLayoutEditor) return <ProfileLayoutEditor onBack={closeProfileLayoutEditor} saving={saving} />;

  // ===== MAIN ADMIN PORTAL MODAL =====
  return (
    <Modal
      title={<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><BgColorsOutlined style={{ color: '#0d47a1', fontSize: '24px' }} /><span style={{ fontSize: '20px', fontWeight: 'bold' }}>Admin Portal</span></div>}
      open={visible}
      onCancel={onClose}
      width={550}
      footer={null}
      centered
    >
      <div style={{ padding: '20px' }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Button type="primary" size="middle" icon={<EditOutlined />} onClick={openEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #0d47a1, #1976d2)', border: 'none', borderRadius: '8px' }}>
              Login Page Editor
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" size="middle" icon={<LayoutOutlined />} onClick={openNavbarEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #0f766e, #0d9488)', border: 'none', borderRadius: '8px' }}>
              Navbar Theme Editor
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" size="middle" icon={<DashboardOutlined />} onClick={openDashboardEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #722ed1, #b37feb)', border: 'none', borderRadius: '8px' }}>
              Dashboard Editor
            </Button>
          </Col>
          {/* <Col span={12}>
            <Button type="primary" size="middle" icon={<LayoutOutlined />} onClick={openLayoutEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #13c2c2, #08979c)', border: 'none', borderRadius: '8px' }}>
              Dashboard Layout
            </Button>
          </Col> */}
          <Col span={12}>
            <Button type="primary" size="middle" icon={<MenuOutlined />} onClick={openSidebarEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #d46b08, #fa8c16)', border: 'none', borderRadius: '8px' }}>
              Sidebar Menu Editor
            </Button>
          </Col>
          {/* <Col span={12}>
            <Button type="primary" size="middle" icon={<LayoutOutlined />} onClick={openProfileLayoutEditor} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', border: 'none', borderRadius: '8px' }}>
              Profile Layout
            </Button>
          </Col> */}
          {/* <Col span={12}>
            <Button type="primary" size="middle" icon={<TeamOutlined />} onClick={openRoleManager} style={{ height: '45px', width: '100%', fontSize: '14px', background: 'linear-gradient(135deg, #d48806, #faad14)', border: 'none', borderRadius: '8px' }}>
              Role Manager
            </Button>
          </Col> */}
          <Col span={24}>
            <Button type="default" size="middle" icon={<KeyOutlined />} onClick={openChangePassword} style={{ height: '45px', width: '100%', fontSize: '14px', border: '2px solid #0d47a1', color: '#0d47a1', borderRadius: '8px' }}>
              Change Password
            </Button>
          </Col>
        </Row>

        <p style={{ marginTop: '20px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
          Customize Login Page | Navbar | Dashboard | Layout | Menu | Profile Layout | Roles | Password
        </p>
      </div>
    </Modal>
  );
};

export default AdminPortal;